import React, { useState } from 'react';
import LabeledDropdown from '../LabeledDropdown';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../../../constants/dataAids';
import PropTypes from 'prop-types';
import Buttons from './Options/Buttons';
import ColorSwatch from '@wsb/guac-widget-shared/lib/components/ColorSwatch';

const Variants = props => {
  const {
    variantCount,
    onChange,
    product,
    translate,
    optionValues,
    selectedVariantOptions,
    requiredFieldsBlank,
    optionOrder,
    isVariantAvailable,
    isVariantInStock,
    handleVariantSelectOption,
    isMinimalView
  } = props;

  const [selectedColor, setSelectedColor] = useState('');
  const [selectedButtons, setSelectedButtons] = useState({});

  if (!product || !product.product_option_order || !variantCount) {
    return null;
  }

  const { Block } = UX2.Element;
  const { Form } = UX2.Group;

  const onButtonSelected = (value, variantIndex, optionId) => {
    setSelectedButtons({ ...selectedButtons, [variantIndex]: value });
    handleVariantSelectOption(variantIndex, optionId);
  };

  const onColorSelected = (value, variantIndex, optionId) => {
    setSelectedColor(value);
    handleVariantSelectOption(variantIndex, optionId);
  };

  const checkInStock = (variantIndex, optionId, isBackOrderable) => {
    if (isVariantAvailable(variantIndex, optionId) && isBackOrderable) return true;

    return isVariantAvailable(variantIndex, optionId) && isVariantInStock(variantIndex, optionId);
  };

  const listItems = [];

  const containerStyles = {
    display: 'flex',
    marginTop: 'xsmall',
    flexWrap: 'wrap'
  };

  const itemMarginBottom = isMinimalView ? 'small' : 'medium';

  for (let variantIndex = 0; variantIndex < variantCount; variantIndex++) {
    const optionType = product.option_types[variantIndex];
    const currentOptionIds = optionOrder && optionOrder[optionType?.id];
    const fieldId = 'variant_select_' + variantIndex;
    const options = [];

    switch (optionType?.display_type) {
      case 'button': {
        listItems.push(
          <Form.Group key={ fieldId } style={{ marginBottom: itemMarginBottom }}>
            <UX2.Element.Details>{ optionType.name }</UX2.Element.Details>
            <UX2.Element.Block style={ containerStyles }>
              { currentOptionIds.map((optionId, index) => (
                <Buttons
                  key={ index }
                  name={ optionValues[optionId].name }
                  isActive={
                    Object.values(selectedButtons).includes(optionValues[optionId].name) &&
                    checkInStock(variantIndex, optionId, product.backorderable)
                  }
                  onChange={ name =>
                    checkInStock(variantIndex, optionId, product.backorderable)
                      ? onButtonSelected(name, variantIndex, optionId)
                      : null
                  }
                  inStock={ checkInStock(variantIndex, optionId, product.backorderable) }
                />
              )) }
            </UX2.Element.Block>
          </Form.Group>
        );
        break;
      }
      case 'dropdown': {
        if (currentOptionIds?.length) {
          // For required option dropdowns, we add "-- Select --" as the default option
          options.push(
            <option key='variant_option_select' value='select'>
              { translate('products.option_select') }
            </option>
          );
        }

        for (const optionId of currentOptionIds) {
          let optionName = optionValues[optionId].name;
          let disabled = false;

          if (isVariantAvailable(variantIndex, optionId)) {
            if (!isVariantInStock(variantIndex, optionId) && product.backorderable) {
              optionName = translate('products.backordered_message', { name: optionName });
            } else if (!isVariantInStock(variantIndex, optionId) && !product.backorderable) {
              optionName = translate('products.out_of_stock_message', { name: optionName });
              disabled = true;
            }

            options.push(
              <option key={ 'variant_option_' + optionId } value={ optionId } disabled={ disabled }>
                { optionName }
              </option>
            );
          }
        }

        const selectedOption = selectedVariantOptions[variantIndex].optionValue;
        const selectedValueForDropdown = selectedOption === null ? 'select' : selectedOption;
        const labelError = Boolean(selectedOption === null && requiredFieldsBlank);
        listItems.push(
          <Form.Group key={ fieldId } style={{ marginBottom: itemMarginBottom }}>
            <LabeledDropdown
              labelText={ optionType.presentation }
              labelError={ labelError }
              id={ fieldId }
              value={ selectedValueForDropdown }
              onChange={ onChange.bind(null, variantIndex) }
              children={ options }
            />
          </Form.Group>
        );
        break;
      }
      case 'color': {
        listItems.push(
          <Form.Group key={ fieldId } style={{ marginBottom: itemMarginBottom }}>
            <UX2.Element.Details>{ `${optionType.name}: ${selectedColor}` }</UX2.Element.Details>
            <UX2.Element.Block
              style={ containerStyles }
              data-aid={ dataAids.PRODUCT_COLOR_SWATCHES_RENDERED }
            >
              { currentOptionIds.map((optionId, index) => (
                <span
                  onClick={ () =>
                    checkInStock(variantIndex, optionId, product.backorderable)
                      ? onColorSelected(optionValues[optionId].name, variantIndex, optionId)
                      : null
                  }
                >
                  <ColorSwatch
                    key={ index }
                    color={ optionValues[optionId].color }
                    isActive={
                      selectedColor === optionValues[optionId].name &&
                      checkInStock(variantIndex, optionId, product.backorderable)
                    }
                    inStock={ checkInStock(variantIndex, optionId, product.backorderable) }
                    isSmall={ false }
                  />
                </span>
              )) }
            </UX2.Element.Block>
          </Form.Group>
        );
        break;
      }
      default:
        break;
    }
  }

  return <Block children={ listItems } />;
};

Variants.propTypes = {
  variantCount: PropTypes.number,
  onChange: PropTypes.func,
  product: PropTypes.object,
  translate: PropTypes.func,
  optionValues: PropTypes.object,
  selectedVariantOptions: PropTypes.array,
  requiredFieldsBlank: PropTypes.bool,
  optionOrder: PropTypes.object,
  isVariantAvailable: PropTypes.func,
  isVariantInStock: PropTypes.func,
  handleVariantSelectOption: PropTypes.func,
  isMinimalView: PropTypes.bool
};

export default Variants;
